import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const CHANNEL_SIZE = 16
const SKIP_OFFSET = 0x017e
const DCS_MASK = 0x2000
const DCS_OCTAL = 0x0800
const DCS_INVERSE = 0x8000
const RT21_DCS_CODES = new Set([
  ...DTCS_CODES,
  17,
  50,
  55,
  135,
  217,
  254,
  305,
  345,
  425,
  466,
  534,
  645,
  765,
])

type RT21Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: string
  fingerprints: readonly string[]
  channels: number
  memorySize: number
  blockSize: 16 | 32 | 64
  ranges: readonly (readonly [number, number])[]
  channelFormat: RT21ChannelFormat
  powerLevels: readonly string[]
  ackFirst?: boolean
  skipOffset?: number | null
  channelOffset?: number
  splitChannelOffset?: number
  splitAfterChannel?: number
  powerTableOffset?: number
  echo?: boolean
}

type RT21ChannelFormat =
  | 'rt21'
  | 'rt29'
  | 'rb26'
  | 'rb17a'
  | 'rt86'
  | 'rt86s'
  | 'rb28'

const RB28_TONES = CTCSS_TONES.filter(
  (tone) =>
    ![
      69.3, 159.8, 165.5, 171.3, 177.3, 183.5, 189.9, 196.6, 199.5,
      206.5, 229.1, 254.1,
    ].includes(tone),
)
const RB28_DTCS = DTCS_CODES.filter(
  (code) =>
    ![
      36, 53, 122, 145, 212, 225, 246, 252, 255, 266, 274, 325, 332,
      356, 446, 452, 454, 455, 462, 523, 526,
    ].includes(code),
)

export type RT21Profile = {
  protocol: 'rt21'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  powerLevels: readonly string[]
  magic: readonly number[]
  fingerprints: readonly (readonly number[])[]
  channelOffset: number
  memorySize: number
  blockSize: 16 | 32 | 64
  ranges: readonly (readonly [number, number])[]
  channelFormat: RT21ChannelFormat
  ackFirst: boolean
  skipOffset: number | null
  splitChannelOffset: number | null
  splitAfterChannel: number | null
  powerTableOffset: number | null
  echo: boolean
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

function createProfile(spec: RT21Spec): RT21Profile {
  return {
    protocol: 'rt21',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      `${spec.channels} channels`,
      'No channel names',
    ],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channels,
    nameLength: 0,
    powerLevels: spec.powerLevels,
    magic: ascii(spec.magic),
    fingerprints: spec.fingerprints.map(ascii),
    channelOffset:
      spec.channelOffset ??
      (spec.channelFormat === 'rb26' ||
      spec.channelFormat === 'rb28' ||
      spec.channelFormat === 'rt86' ||
      spec.channelFormat === 'rt86s'
        ? 0
        : 0x10),
    splitChannelOffset: spec.splitChannelOffset ?? null,
    splitAfterChannel: spec.splitAfterChannel ?? null,
    memorySize: spec.memorySize,
    blockSize: spec.blockSize,
    ranges: spec.ranges,
    channelFormat: spec.channelFormat,
    ackFirst: spec.ackFirst ?? true,
    skipOffset:
      spec.skipOffset === undefined ? SKIP_OFFSET : spec.skipOffset,
    powerTableOffset: spec.powerTableOffset ?? null,
    echo: spec.echo ?? false,
  }
}

export const RT21_PROFILES: readonly RT21Profile[] = [
  createProfile({
    id: 'retevis-rt21',
    vendor: 'Retevis',
    model: 'RT21',
    summary: 'Retevis RT21 compact UHF business radio.',
    magic: 'PRMZUNE',
    fingerprints: ['P3207s\u00f8\u00ff'],
    channels: 16,
    memorySize: 0x0400,
    blockSize: 16,
    ranges: [[0x0000, 0x0400]],
    channelFormat: 'rt21',
    powerLevels: ['High', 'Low'],
  }),
  createProfile({
    id: 'retevis-rt21v',
    vendor: 'Retevis',
    model: 'RT21V',
    summary: 'Retevis RT21V compact VHF radio.',
    magic: 'PRMZUNE',
    fingerprints: ['P2207\u0001\u00f8\u00ff'],
    channels: 5,
    memorySize: 0x0400,
    blockSize: 16,
    ranges: [[0x0000, 0x0400]],
    channelFormat: 'rt21',
    powerLevels: ['High', 'Low'],
  }),
  createProfile({
    id: 'retevis-rt29-uhf',
    vendor: 'Retevis',
    model: 'RT29_UHF',
    summary: 'Retevis RT29 UHF radio with three transmit power levels.',
    magic: 'PROHRAM',
    fingerprints: ['P3207\u0013\u00f8\u00ff', 'P3207s\u00f8\u00ff'],
    channels: 16,
    memorySize: 0x0400,
    blockSize: 64,
    ranges: [[0x0000, 0x0300]],
    channelFormat: 'rt29',
    powerLevels: ['High', 'Mid', 'Low'],
  }),
  createProfile({
    id: 'retevis-rt29-vhf',
    vendor: 'Retevis',
    model: 'RT29_VHF',
    summary: 'Retevis RT29 VHF radio with three transmit power levels.',
    magic: 'PROHRAM',
    fingerprints: ['P2207\u0001\u00f8\u00ff'],
    channels: 16,
    memorySize: 0x0400,
    blockSize: 64,
    ranges: [[0x0000, 0x0300]],
    channelFormat: 'rt29',
    powerLevels: ['High', 'Mid', 'Low'],
  }),
  createProfile({
    id: 'retevis-rb23',
    vendor: 'Retevis',
    model: 'RB23',
    summary: 'Retevis RB23 compact GMRS business radio.',
    magic: 'PHOGR\u00010',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 30,
    memorySize: 0x0320,
    blockSize: 32,
    ranges: [[0x0000, 0x0320]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x01f0,
  }),
  createProfile({
    id: 'retevis-rb26',
    vendor: 'Retevis',
    model: 'RB26',
    summary: 'Retevis RB26 compact business radio.',
    magic: 'PHOGR\u00010',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 30,
    memorySize: 0x0800,
    blockSize: 32,
    ranges: [[0x0000, 0x0320]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x01f0,
  }),
  createProfile({
    id: 'retevis-rb626',
    vendor: 'Retevis',
    model: 'RB626',
    summary: 'Retevis RB626 compact business radio.',
    magic: 'PHOGR\u00010',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 32,
    memorySize: 0x0800,
    blockSize: 32,
    ranges: [[0x0000, 0x0340]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x0200,
  }),
  createProfile({
    id: 'retevis-rb89',
    vendor: 'Retevis',
    model: 'RB89',
    summary: 'Retevis RB89 compact business radio.',
    magic: 'PHOGR\u00010',
    fingerprints: ['P32073\u0001\u00ff'],
    channels: 30,
    memorySize: 0x0340,
    blockSize: 32,
    ranges: [[0x0000, 0x0330]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x01f0,
  }),
  createProfile({
    id: 'retevis-rt19',
    vendor: 'Retevis',
    model: 'RT19',
    summary: 'Retevis RT19 compact business radio.',
    magic: 'PHOGRQ^',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 22,
    memorySize: 0x0180,
    blockSize: 32,
    ranges: [[0x0000, 0x0180]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
  createProfile({
    id: 'retevis-rt619',
    vendor: 'Retevis',
    model: 'RT619',
    summary: 'Retevis RT619 compact business radio.',
    magic: 'PHOGRS]',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 16,
    memorySize: 0x0120,
    blockSize: 32,
    ranges: [[0x0000, 0x0120]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
  createProfile({
    id: 'retevis-rt76',
    vendor: 'Retevis',
    model: 'RT76',
    summary: 'Retevis RT76 compact business radio.',
    magic: 'PHOGR\u0014\u00d4',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 30,
    memorySize: 0x01e0,
    blockSize: 32,
    ranges: [[0x0000, 0x01e0]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
  createProfile({
    id: 'abbree-ar-63',
    vendor: 'Abbree',
    model: 'AR-63',
    summary: 'Abbree AR-63 compact business radio.',
    magic: 'PHOGR\u00f5\u009a',
    fingerprints: ['P32073\u0002\u00ff', 'P32073\u0003\u00ff'],
    channels: 16,
    memorySize: 0x0140,
    blockSize: 32,
    ranges: [[0x0000, 0x0140]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x004e,
  }),
  createProfile({
    id: 'retevis-rb28b',
    vendor: 'Retevis',
    model: 'RB28B',
    summary: 'Retevis RB28B compact FRS business radio.',
    magic: 'PHOGR\u0008\u00b2',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 22,
    memorySize: 0x01f0,
    blockSize: 32,
    ranges: [[0x0000, 0x01f0]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
  createProfile({
    id: 'retevis-rb628b',
    vendor: 'Retevis',
    model: 'RB628B',
    summary: 'Retevis RB628B compact PMR business radio.',
    magic: 'PHOGR\u0009\u00b2',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 16,
    memorySize: 0x01f0,
    blockSize: 32,
    ranges: [[0x0000, 0x01f0]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
  createProfile({
    id: 'retevis-rt86',
    vendor: 'Retevis',
    model: 'RT86',
    summary: 'Retevis RT86 compact business radio.',
    magic: 'PHOGR\u00cd\u0091',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 16,
    memorySize: 0x01a0,
    blockSize: 32,
    ranges: [[0x0000, 0x01a0]],
    channelFormat: 'rt86',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x004e,
  }),
  createProfile({
    id: 'retevis-rt40b',
    vendor: 'Retevis',
    model: 'RT40B',
    summary: 'Retevis RT40B compact GMRS business radio.',
    magic: 'PHOGRH\u005c',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 22,
    memorySize: 0x0160,
    blockSize: 32,
    ranges: [[0x0000, 0x0160]],
    channelFormat: 'rb26',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: 0x00ad,
    echo: true,
  }),
  createProfile({
    id: 'retevis-rb17a',
    vendor: 'Retevis',
    model: 'RB17A',
    summary: 'Retevis RB17A compact GMRS business radio.',
    magic: 'PROA8US',
    fingerprints: ['P3217s\u00f8\u00ff'],
    channels: 30,
    memorySize: 0x0300,
    blockSize: 64,
    ranges: [[0x0000, 0x0300]],
    channelFormat: 'rb17a',
    powerLevels: ['High', 'Low'],
    channelOffset: 0x0010,
    splitChannelOffset: 0x0200,
    splitAfterChannel: 16,
    skipOffset: 0x017e,
  }),
  createProfile({
    id: 'retevis-rt86s',
    vendor: 'Retevis',
    model: 'RT86S',
    summary: 'Retevis RT86S compact business radio.',
    magic: 'PHOGR\u00a7\u00b8',
    fingerprints: ['P32073\u0000\u00ff', 'P32073\u0003\u00ff'],
    channels: 30,
    memorySize: 0x0360,
    blockSize: 32,
    ranges: [[0x0000, 0x0350]],
    channelFormat: 'rt86s',
    powerLevels: ['High', 'Mid', 'Low'],
    ackFirst: false,
    skipOffset: 0x01f0,
    powerTableOffset: 0x0330,
  }),
  createProfile({
    id: 'retevis-rt86a',
    vendor: 'Retevis',
    model: 'RT86A',
    summary: 'Retevis RT86A compact business radio.',
    magic: 'PHOGR6\u0083',
    fingerprints: ['P32073\u0002\u00ff'],
    channels: 30,
    memorySize: 0x0360,
    blockSize: 32,
    ranges: [[0x0000, 0x0350]],
    channelFormat: 'rt86s',
    powerLevels: ['High', 'Mid', 'Low'],
    ackFirst: false,
    skipOffset: 0x01f0,
    powerTableOffset: 0x0330,
  }),
  createProfile({
    id: 'retevis-rb28',
    vendor: 'Retevis',
    model: 'RB28',
    summary: 'Retevis RB28 compact FRS business radio.',
    magic: 'PHOGR\u000b(',
    fingerprints: ['P32073\u0002\u00ff', 'P32073\u0000\u00ff'],
    channels: 22,
    memorySize: 0x0160,
    blockSize: 32,
    ranges: [[0x0000, 0x0160]],
    channelFormat: 'rb28',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
  createProfile({
    id: 'retevis-rb628',
    vendor: 'Retevis',
    model: 'RB628',
    summary: 'Retevis RB628 compact PMR business radio.',
    magic: 'PHOGR(\u000b',
    fingerprints: ['P32073\u0000\u00ff'],
    channels: 16,
    memorySize: 0x0160,
    blockSize: 32,
    ranges: [[0x0000, 0x0160]],
    channelFormat: 'rb28',
    powerLevels: ['High', 'Low'],
    ackFirst: false,
    skipOffset: null,
  }),
]

function emptyChannel(
  number: number,
  profile: RT21Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function decodeDcs(value: number): { code: number; reversed: boolean } {
  const reversed = (value & DCS_INVERSE) !== 0
  const rawCode = value & 0x07ff
  let code: number
  if (value & DCS_OCTAL) {
    code = Number.parseInt(rawCode.toString(8).padStart(3, '0'), 10)
  } else {
    const hexDigits = rawCode.toString(16).padStart(3, '0')
    const candidate = /[a-f]/.test(hexDigits)
      ? -1
      : Number.parseInt(hexDigits, 10)
    code = candidate < 0 || !RT21_DCS_CODES.has(candidate)
      ? Number.parseInt(rawCode.toString(8).padStart(3, '0'), 10)
      : candidate
  }
  return { code, reversed }
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value > DCS_MASK) {
    const decoded = decodeDcs(value)
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: decoded.code,
      dtcsPolarity: decoded.reversed ? 'R' : 'N',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    value =
      DCS_OCTAL |
      Number.parseInt(channel.dtcs.toString(), 8) |
      (channel.dtcsPolarity === 'R' ? DCS_INVERSE : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function decodeRb28Tone(value: number): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (value === 0 || value === 0xff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value < 0x27) {
    return {
      toneMode: 'ctcss',
      ctcss: RB28_TONES[value - 1] ?? null,
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  return {
    toneMode: 'dtcs',
    ctcss: null,
    dtcs: RB28_DTCS[value - 0x27] ?? null,
    dtcsPolarity: 'N',
  }
}

function encodeRb28Tone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = RB28_TONES.indexOf(
      channel.ctcss as (typeof RB28_TONES)[number],
    )
    if (index < 0) throw new Error('That CTCSS tone is not supported.')
    value = index + 1
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = RB28_DTCS.indexOf(
      channel.dtcs as (typeof RB28_DTCS)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported.')
    value = index + 0x27
  }
  return [value, 0]
}

function powerFromFlags(
  flags: number,
  profile: RT21Profile,
  image: Uint8Array,
  number: number,
): string {
  if (profile.channelFormat === 'rt29') {
    const power = (flags >> 5) & 0x03
    return (
      profile.powerLevels[power === 1 ? 0 : power === 0 ? 1 : 2] ??
      profile.powerLevels[0]
    )
  }
  if (profile.channelFormat === 'rt86s' && profile.powerTableOffset !== null) {
    const power = image[profile.powerTableOffset + number - 1]
    return (
      profile.powerLevels[power === 2 ? 0 : power === 1 ? 1 : 2] ??
      profile.powerLevels[2]
    )
  }
  const high =
    profile.channelFormat === 'rb17a'
      ? (flags & 0x40) !== 0
      : profile.channelFormat === 'rt86'
        ? (flags & 0x04) !== 0
        : (flags & 0x20) !== 0
  return high
    ? profile.powerLevels[0]
    : profile.powerLevels[1]
}

function channelOffset(profile: RT21Profile, number: number): number {
  if (
    profile.splitChannelOffset !== null &&
    profile.splitAfterChannel !== null &&
    number > profile.splitAfterChannel
  ) {
    return (
      profile.splitChannelOffset +
      (number - profile.splitAfterChannel - 1) * CHANNEL_SIZE
    )
  }
  return profile.channelOffset + (number - 1) * CHANNEL_SIZE
}

function flagOffset(profile: RT21Profile): number {
  return profile.channelFormat === 'rb26' ||
    profile.channelFormat === 'rb28' ||
    profile.channelFormat === 'rb17a' ||
    profile.channelFormat === 'rt86' ||
    profile.channelFormat === 'rt86s'
    ? 12
    : 13
}

function powerAndWideMasks(profile: RT21Profile): readonly [number, number] {
  switch (profile.channelFormat) {
    case 'rb17a':
      return [0x40, 0x80]
    case 'rt86':
    case 'rt86s':
      return [0x04, 0x08]
    case 'rt29':
      return [0x60, 0x10]
    default:
      return [0x20, 0x10]
  }
}

export function parseRT21Channel(
  image: Uint8Array,
  profile: RT21Profile,
  number: number,
): WebChannel {
  const offset = channelOffset(profile, number)
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (raw.byteLength !== CHANNEL_SIZE || !rxFrequency) {
    return emptyChannel(number, profile)
  }
  const flags = raw[flagOffset(profile)]
  const skipByte =
    profile.skipOffset === null
      ? 0xff
      : (image[profile.skipOffset + ((number - 1) >> 3)] ?? 0)
  let tone: Pick<
    WebChannel,
    'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
  >
  if (profile.channelFormat === 'rb28') {
    const txTone = decodeRb28Tone(raw[10])
    tone = txTone.toneMode === 'none' ? decodeRb28Tone(raw[8]) : txTone
  } else {
    tone = decodeTone(raw.slice(8, 10))
  }
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...tone,
    mode: (flags & powerAndWideMasks(profile)[1]) !== 0 ? 'FM' : 'NFM',
    power: powerFromFlags(flags, profile, image, number),
    skip:
      profile.skipOffset === null
        ? false
        : (skipByte & (1 << ((number - 1) & 7))) === 0,
  }
}

export function parseRT21Channels(
  image: Uint8Array,
  profile: RT21Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseRT21Channel(image, profile, index + 1),
  )
}

export function applyRT21Channel(
  image: Uint8Array,
  profile: RT21Profile,
  channel: WebChannel,
): void {
  const offset = channelOffset(profile, channel.number)
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    if (profile.skipOffset !== null) {
      const skipOffset = profile.skipOffset + ((channel.number - 1) >> 3)
      image[skipOffset] &= ~(1 << ((channel.number - 1) & 7))
    }
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const wasEmpty = raw[0] === 0xff
  if (wasEmpty) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (profile.channelFormat === 'rb28') {
    const tone = encodeRb28Tone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  } else {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  const flagsOffset = flagOffset(profile)
  const [powerMask, wideMask] = powerAndWideMasks(profile)
  const preservedFlags = wasEmpty
    ? 0
    : raw[flagsOffset] & ~(powerMask | wideMask)
  if (profile.channelFormat === 'rt29') {
    const powerIndex = Math.max(0, profile.powerLevels.indexOf(channel.power))
    const power = powerIndex === 0 ? 1 : powerIndex === 1 ? 0 : 2
    raw[flagsOffset] =
      preservedFlags |
      ((power & 0x03) << 5) |
      (channel.mode === 'FM' ? wideMask : 0)
  } else if (profile.channelFormat === 'rt86s') {
    raw[flagsOffset] =
      preservedFlags | (channel.mode === 'FM' ? wideMask : 0)
  } else {
    raw[flagsOffset] =
      preservedFlags |
      (channel.power === profile.powerLevels[0] ? powerMask : 0) |
      (channel.mode === 'FM' ? wideMask : 0)
  }
  if (profile.channelFormat === 'rt86s' && profile.powerTableOffset !== null) {
    const powerIndex = Math.max(0, profile.powerLevels.indexOf(channel.power))
    image[profile.powerTableOffset + channel.number - 1] =
      powerIndex === 0 ? 2 : powerIndex === 1 ? 1 : 0
  }
  if (profile.skipOffset !== null) {
    const skipOffset = profile.skipOffset + ((channel.number - 1) >> 3)
    const bit = 1 << ((channel.number - 1) & 7)
    image[skipOffset] = channel.skip
      ? image[skipOffset] & ~bit
      : image[skipOffset] | bit
  }
}

function containsBytes(haystack: Uint8Array, needle: Uint8Array): boolean {
  for (let start = 0; start <= haystack.length - needle.length; start += 1) {
    if (needle.every((value, index) => haystack[start + index] === value)) {
      return true
    }
  }
  return false
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 2000))[0] !== ACK) {
    throw new Error(message)
  }
}

async function consumeEcho(
  profile: RT21Profile,
  transport: RadioTransport,
  size: number,
): Promise<void> {
  if (profile.echo) await transport.readExact(size, 1500)
}

async function enterProgrammingMode(
  profile: RT21Profile,
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  await consumeEcho(profile, transport, profile.magic.length)
  await readAck(transport, 'The radio refused RT21 programming mode.')
  await transport.write(Uint8Array.of(0x02))
  await consumeEcho(profile, transport, 1)
  const identity = await transport.readExact(8, 3000)
  if (
    !profile.fingerprints.some((fingerprint) =>
      containsBytes(identity, Uint8Array.from(fingerprint)),
    )
  ) {
    throw new Error(
      `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
    )
  }
  await transport.write(Uint8Array.of(ACK))
  await consumeEcho(profile, transport, 1)
  await readAck(transport, 'The radio rejected the RT21 programming session.')
  return new TextDecoder('ascii')
    .decode(identity)
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
}

async function exitProgrammingMode(
  profile: RT21Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
  await consumeEcho(profile, transport, 1)
}

function commandFrame(
  command: number,
  address: number,
  size: number,
): Uint8Array {
  return Uint8Array.of(
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    size,
  )
}

async function readBlock(
  profile: RT21Profile,
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = commandFrame(0x52, address, profile.blockSize)
  await transport.write(command)
  await consumeEcho(profile, transport, command.length)
  const response = await transport.readExact(profile.blockSize + 4, 3000)
  if (
    response[0] !== 0x57 ||
    !command.slice(1).every((value, index) => response[index + 1] === value)
  ) {
    throw new Error(
      `Unexpected RT21 read reply at 0x${address.toString(16)}.`,
    )
  }
  if (address !== 0 || profile.ackFirst) {
    await transport.write(Uint8Array.of(ACK))
    await consumeEcho(profile, transport, 1)
    await readAck(
      transport,
      `The radio rejected read block 0x${address.toString(16)}.`,
    )
  }
  return response.slice(4)
}

async function writeBlock(
  profile: RT21Profile,
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + data.byteLength)
  frame.set(commandFrame(0x57, address, data.byteLength))
  frame.set(data, 4)
  await transport.write(frame)
  await consumeEcho(profile, transport, frame.length)
  await readAck(transport, `The radio rejected write block 0x${address.toString(16)}.`)
}

export async function readRT21RadioWithTransport(
  profile: RT21Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await enterProgrammingMode(profile, transport)
  try {
    const total = Math.ceil(profile.memorySize / profile.blockSize)
    const image = new Uint8Array(total * profile.blockSize).fill(0xff)
    for (let index = 0; index < total; index += 1) {
      const address = index * profile.blockSize
      image.set(await readBlock(profile, transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading Retevis RT21 memory',
      })
    }
    return {
      image: image.slice(0, profile.memorySize),
      reportedModel: reportedModel || profile.model,
    }
  } finally {
    await exitProgrammingMode(profile, transport)
  }
}

export async function writeRT21RadioWithTransport(
  profile: RT21Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const addresses = profile.ranges.flatMap(([start, end]) =>
      Array.from(
        { length: Math.ceil((end - start) / 16) },
        (_, index) => start + index * 16,
      ),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        profile,
        transport,
        address,
        image.slice(address, address + 16),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing Retevis RT21 memory',
      })
    }
  } finally {
    await exitProgrammingMode(profile, transport)
  }
}
